import React, { useEffect, useState } from 'react';
import { healthsherpa } from './api';
import { usePersonColors } from './HealthSherpaPerson';
import { fs } from './hsScale';

// =============================================================================
// 5.47.0 HealthSherpa RENEWAL BONUS (plan year 2027; services/healthsherpa/renewal_bonus.py)
//  RenewalBonusPanel — in the client's workspace, once the 2027 renewal is Finalized: "+ Bonus" (members ticked / unticked,
//    amount per member from the admin's list or typed, max $60 per member per employee), the lines of every employee on it
//    (a split = several lines), admin adjustments (+/- dated lines with a reason).
//  RenewalBonusTab — employees: their own list by carrier with per-carrier + overall totals (member counts);
//    admins: everyone (member names), search, duplicate flags, cancellation review, per-employee Send to bonus, the
//    renewal payouts (Finalize / Revert; Mark paid is on the Bonuses → Payout batches page like any batch), amount list.
// =============================================================================

const rb = () => healthsherpa.renewalBonus;
const detailOf = (e) => e?.response?.data?.detail || e?.message || 'Something went wrong';
const money = (v) => { const n = Number(v || 0); return `${n < 0 ? '−' : ''}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
const box = (c) => ({ background: c.panel, border: `1px solid ${c.border}`, borderRadius: 8, padding: 14 });
const btn = (c, primary, disabled) => ({
  padding: '5px 11px', fontSize: fs(13), borderRadius: 5, cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap',
  fontWeight: primary ? 600 : 400, opacity: disabled ? 0.55 : 1,
  border: primary ? '1px solid #185FA5' : `1px solid ${c.border}`, background: primary ? '#185FA5' : c.panel, color: primary ? '#fff' : c.text,
});
const input = (c, width) => ({ padding: '5px 8px', fontSize: fs(13), borderRadius: 5, border: `1px solid ${c.border}`, background: c.panel,
  color: c.text, width: width || 110, boxSizing: 'border-box' });
const H = ({ c, children, right }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
    <div style={{ fontSize: fs(12), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: c.sub, flex: 1 }}>{children}</div>
    {right}
  </div>
);
const Err = ({ msg }) => (msg ? <div role="alert" style={{ background: '#A32D2D', color: '#fff', padding: '6px 10px', borderRadius: 5, fontSize: fs(13), margin: '6px 0' }}>{msg}</div> : null);
const STATUS_WORD = { open: 'Open', sent: 'Sent to bonus', paid: 'Paid' };

function AmountPicker({ c, amounts, value, onChange, cap, testid }) {
  const preset = amounts.includes(value);
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <select value={preset ? value : 'custom'} onChange={(e) => onChange(e.target.value === 'custom' ? '' : e.target.value)} style={input(c, 120)}
        aria-label="Amount per member" data-testid={`${testid}-pick`}>
        {amounts.map((a) => <option key={a} value={a}>{money(a)} / member</option>)}
        <option value="custom">Other amount…</option>
      </select>
      {!preset && <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={`max ${money(cap)}`} style={input(c, 90)}
        aria-label="Own amount per member" data-testid={`${testid}-own`} inputMode="decimal" />}
    </span>
  );
}

// --- the client's panel (workspace) ------------------------------------------------------------------------------------
export function RenewalBonusPanel({ c, personId, year = 2027, refreshKey }) {
  const [v, setV] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [amt, setAmt] = useState('');
  const [ticks, setTicks] = useState({});
  const [staffId, setStaffId] = useState('');
  const [staff, setStaff] = useState([]);
  const [edit, setEdit] = useState({});
  const [adj, setAdj] = useState(null);
  const load = () => rb().person(personId, year).then((r) => { setV(r.data); setErr(null); }).catch((e) => setErr(detailOf(e)));
  useEffect(() => { load(); }, [personId, year, refreshKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (v && v.is_admin && !staff.length) rb().staff().then((r) => setStaff(r.data.staff)).catch(() => {}); }, [v]);   // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (fn) => {
    setBusy(true); setErr(null);
    try { const r = await fn(); if (r && r.data && r.data.plan_year) setV(r.data); else await load(); return true; } catch (e) { setErr(detailOf(e)); return false; } finally { setBusy(false); }
  };
  if (!v) return err ? <div style={box(c)}><Err msg={err} /></div> : null;
  if (!v.finalized && !v.bonus) return null;                       // "+ Bonus" appears once the renewal is Finalized
  const b = v.bonus;
  const members = b ? b.members : (v.proposed_members || []);
  const startAdd = () => { setAdding(true); setAmt(v.amounts[0] || ''); setTicks(Object.fromEntries(members.map((m) => [m.key, m.ticked]))); };
  const tickedCount = b ? b.ticked : Object.values(ticks).filter(Boolean).length;
  const canMembers = b && !b.locked && (v.is_admin || b.has_mine);
  return (
    <div style={box(c)} data-testid="rb-panel">
      <H c={c} right={!adding && !(b && b.locked) && !(b && b.has_mine && !v.is_admin) && (
        <button style={btn(c, true, busy)} disabled={busy} onClick={startAdd} data-testid="rb-add">
          {b ? (v.is_admin ? '+ Add employee' : '+ Add my share') : '+ Bonus'}</button>)}>
        {year} renewal bonus
      </H>
      {!b && !adding && <div style={{ fontSize: fs(13), color: c.sub }}>No renewal bonus yet.</div>}
      {b && b.admin_note && (
        <div style={{ fontSize: fs(12), color: c.sub, border: `1px dashed ${c.border}`, borderRadius: 5, padding: '4px 8px', marginBottom: 6 }} data-testid="rb-frozen-note">
          {b.admin_note}</div>
      )}
      {b && b.cancel && (
        <div style={{ fontSize: fs(12), color: '#b45309', marginBottom: 6 }} data-testid="rb-cancel-flag">
          ⚠ Policy now {b.cancel.status} (flagged {b.cancel.flagged_label}){b.cancel.reviewed ? ' — reviewed' : ' — waiting for admin review'}</div>
      )}
      <div style={{ fontSize: fs(13), marginBottom: 8 }} data-testid="rb-members">
        <span style={{ color: c.sub }}>Members ({b ? b.members_source : v.members_source} · {tickedCount} counted): </span>
        {members.map((m) => {
          const on = adding && !b ? !!ticks[m.key] : m.ticked;
          const editable = (adding && !b) || canMembers;
          return (
            <label key={m.key} style={{ marginRight: 12, whiteSpace: 'nowrap', opacity: on ? 1 : 0.55 }}>
              <input type="checkbox" checked={on} disabled={!editable || busy} data-testid={`rb-member-${m.key}`}
                onChange={(e) => (adding && !b ? setTicks({ ...ticks, [m.key]: e.target.checked })
                  : run(() => rb().members(personId, year, { [m.key]: e.target.checked })))} /> {m.name}
            </label>
          );
        })}
      </div>
      {b && b.lines.map((ln) => (
        <div key={ln.id} style={{ borderTop: `1px solid ${c.border}`, padding: '6px 0', fontSize: fs(13) }} data-testid={`rb-line-${ln.id}`}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong>{ln.staff}</strong>
            {edit[ln.id] !== undefined
              ? <>
                <AmountPicker c={c} amounts={v.amounts} value={edit[ln.id]} onChange={(x) => setEdit({ ...edit, [ln.id]: x })} cap={v.cap} testid={`rb-edit-${ln.id}`} />
                <button style={btn(c, true, busy)} disabled={busy} data-testid={`rb-edit-save-${ln.id}`}
                  onClick={async () => { if (await run(() => rb().editLine(ln.id, edit[ln.id]))) setEdit({ ...edit, [ln.id]: undefined }); }}>Save</button>
                <button style={btn(c)} onClick={() => setEdit({ ...edit, [ln.id]: undefined })}>Cancel</button>
              </>
              : <span>{money(ln.per_member)} / member</span>}
            <span>= <strong data-testid={`rb-line-total-${ln.id}`}>{money(ln.total)}</strong></span>
            <span style={{ color: c.sub }}>{STATUS_WORD[ln.status]}{ln.payout_batch_id ? ` · payout #${ln.payout_batch_id}` : ''}</span>
            {ln.can_edit && edit[ln.id] === undefined && (
              <button style={btn(c)} onClick={() => setEdit({ ...edit, [ln.id]: ln.per_member })} data-testid={`rb-edit-${ln.id}`}>Edit</button>)}
            {ln.can_remove && edit[ln.id] === undefined && (   /* 5.48.1: not while another share of the client is sent */
              <button style={btn(c)} disabled={busy} onClick={() => { if (window.confirm('Remove this bonus line?')) run(() => rb().removeLine(ln.id)); }}>Remove</button>)}
            {v.is_admin && ln.status === 'open' && <button style={btn(c)} onClick={() => setAdj({ line: ln.id, amount: '', reason: '', member: '' })} data-testid={`rb-adjust-${ln.id}`}>Adjustment…</button>}
          </div>
          {(ln.adjustments || []).map((a) => (
            <div key={a.id} style={{ fontSize: fs(12), color: c.sub, marginLeft: 14 }} data-testid="rb-adjustment">
              {a.at_label} · {Number(a.amount) > 0 ? '+' : ''}{money(a.amount)} · {a.reason}{a.member ? ` (${a.member})` : ''} · by {a.by}
              {a.can_remove && <button style={{ ...btn(c), padding: '0 6px', marginLeft: 6, fontSize: fs(11) }} disabled={busy} data-testid={`rb-adj-remove-${a.id}`}
                onClick={() => { if (window.confirm('Remove this adjustment?')) run(() => rb().removeAdjustment(a.id)); }}>Remove</button>}</div>
          ))}
          {adj && adj.line === ln.id && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
              <input value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} placeholder="-5.00" style={input(c, 80)} aria-label="Adjustment amount" data-testid="rb-adj-amount" />
              <input value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} placeholder="Reason (e.g. cancelled month)" style={input(c, 240)} aria-label="Reason" data-testid="rb-adj-reason" />
              <select value={adj.member} onChange={(e) => setAdj({ ...adj, member: e.target.value })} style={input(c, 160)} aria-label="Member (optional)">
                <option value="">(whole household)</option>
                {members.map((m) => <option key={m.key} value={m.key}>{m.name}</option>)}
              </select>
              <button style={btn(c, true, busy)} disabled={busy} data-testid="rb-adj-save"
                onClick={async () => { if (await run(() => rb().adjust(adj.line, { amount: adj.amount, reason: adj.reason, member_key: adj.member || null }))) setAdj(null); }}>Add</button>
              <button style={btn(c)} onClick={() => setAdj(null)}>Cancel</button>
            </div>
          )}
        </div>
      ))}
      {adding && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', borderTop: `1px solid ${c.border}`, paddingTop: 8 }}>
          {v.is_admin && (
            <select value={staffId} onChange={(e) => setStaffId(e.target.value)} style={input(c, 180)} aria-label="Employee" data-testid="rb-staff">
              <option value="">(me)</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <AmountPicker c={c} amounts={v.amounts} value={amt} onChange={setAmt} cap={v.cap} testid="rb-amount" />
          <span style={{ fontSize: fs(13) }}>× {tickedCount} = <strong>{money((Number(amt) || 0) * tickedCount)}</strong></span>
          <button style={btn(c, true, busy)} disabled={busy || !amt} data-testid="rb-save"
            onClick={async () => { if (await run(() => rb().add(personId, { plan_year: year, per_member: amt, staff_user_id: staffId ? Number(staffId) : null, ticks: b ? null : ticks }))) setAdding(false); }}>Save</button>
          <button style={btn(c)} onClick={() => setAdding(false)}>Cancel</button>
          <span style={{ fontSize: fs(12), color: c.sub }}>Max {money(v.cap)} per member per employee.</span>
        </div>
      )}
      <Err msg={err} />
    </div>
  );
}

// --- the tab ---------------------------------------------------------------------------------------------------------------
function Groups({ c, data, admin }) {
  if (!data) return null;
  if (!data.count) return <div style={{ fontSize: fs(13), color: c.sub }}>Nothing yet.</div>;
  const th = { textAlign: 'left', padding: '4px 8px', fontSize: fs(12), color: c.sub, borderBottom: `1px solid ${c.border}` };
  const td = { padding: '4px 8px', fontSize: fs(13), borderBottom: `1px solid ${c.border}`, verticalAlign: 'top' };
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {data.groups.map((g) => (
        <div key={g.carrier} data-testid="rb-group">
          <div style={{ fontWeight: 700, fontSize: fs(14), marginBottom: 4 }}>{g.carrier} — {money(g.total)} <span style={{ color: c.sub, fontWeight: 400 }}>({g.rows.length} client(s), {g.members} member(s))</span></div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>{['Client', ...(admin ? ['Employee'] : []), admin ? 'Members' : 'Members', 'Per member', 'Adjustments', 'Total', 'Status'].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {g.rows.map((r) => (
                <tr key={r.line_id} data-testid="rb-row">
                  <td style={td}>{r.client}{r.cancel_open && <span title="Policy terminated / cancelled — review" style={{ color: '#b45309' }}> ⚠</span>}</td>
                  {admin && <td style={td}>{r.staff}</td>}
                  <td style={td}>{r.members}{admin && r.member_names ? <div style={{ fontSize: fs(11), color: c.sub }}>{r.member_names.join(', ')}</div> : null}</td>
                  <td style={td}>{money(r.per_member)}</td>
                  <td style={td}>{Number(r.adjustments) ? money(r.adjustments) : '—'}</td>
                  <td style={td}><strong>{money(r.total)}</strong></td>
                  <td style={td}>{STATUS_WORD[r.status]}{r.payout_batch_id ? ` #${r.payout_batch_id}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      <div style={{ fontWeight: 700, fontSize: fs(15) }} data-testid="rb-overall">Overall: {money(data.total)} ({data.count} line(s), {data.members} member(s))</div>
    </div>
  );
}

export function RenewalBonusTab({ darkMode, isAdmin }) {
  const c = usePersonColors(darkMode);
  const year = 2027;
  const [mine, setMine] = useState(null);
  const [data, setData] = useState(null);
  const [f, setF] = useState({ q: '', member: '', staff_user_id: '', carrier: '', amount: '', status: '' });
  const [staff, setStaff] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [amountsText, setAmountsText] = useState('');
  const loadMine = () => rb().mine(year).then((r) => setMine(r.data)).catch((e) => setErr(detailOf(e)));
  const search = () => rb().search({ plan_year: year, ...f, staff_user_id: f.staff_user_id ? Number(f.staff_user_id) : null })
    .then((r) => { setData(r.data); setAmountsText(r.data.amounts.join(', ')); setErr(null); }).catch((e) => setErr(detailOf(e)));
  useEffect(() => {
    loadMine();
    if (isAdmin) { search(); rb().staff().then((r) => setStaff(r.data.staff)).catch(() => {}); }
  }, [isAdmin]);   // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (fn, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true); setErr(null);
    try { await fn(); await search(); await loadMine(); } catch (e) { setErr(detailOf(e)); }
    setBusy(false);
  };
  const field = (k, label, w) => <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={label} aria-label={label}
    style={input(c, w)} data-testid={`rb-f-${k}`} onKeyDown={(e) => { if (e.key === 'Enter') search(); }} />;
  return (
    <div style={{ padding: 18, display: 'grid', gap: 14, color: c.text }} data-testid="rb-tab">
      <Err msg={err} />
      <div style={box(c)}>
        <H c={c}>My {year} renewal bonus</H>
        <Groups c={c} data={mine} admin={false} />
      </div>
      {isAdmin && data && (
        <>
          <div style={box(c)} data-testid="rb-admin">
            <H c={c}>Everyone · {year}</H>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {field('q', 'Client name', 160)}{field('member', 'Member name', 160)}
              <select value={f.staff_user_id} onChange={(e) => setF({ ...f, staff_user_id: e.target.value })} style={input(c, 170)} aria-label="Employee" data-testid="rb-f-staff">
                <option value="">All employees</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {field('carrier', 'Carrier', 130)}{field('amount', 'Amount', 90)}
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} style={input(c, 120)} aria-label="Status" data-testid="rb-f-status">
                <option value="">Any status</option><option value="open">Open</option><option value="sent">Sent</option><option value="paid">Paid</option>
              </select>
              <button style={btn(c, true)} onClick={search} data-testid="rb-search">Search</button>
            </div>
            <Groups c={c} data={data} admin />
          </div>
          <div style={box(c)} data-testid="rb-staff-totals">
            <H c={c}>Per employee — Send to bonus</H>
            {data.staff.map((s) => (
              <div key={s.staff_user_id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: fs(13), padding: '3px 0' }}>
                <strong style={{ minWidth: 180 }}>{s.staff}</strong>
                <span>open {money(s.open_total)} ({s.open_count})</span><span style={{ color: c.sub }}>sent {money(s.sent_total)} · paid {money(s.paid_total)}</span>
                {s.open_count > 0 && <button style={btn(c, true, busy)} disabled={busy} data-testid={`rb-send-${s.staff_user_id}`}
                  onClick={() => act(() => rb().send(s.staff_user_id, year), `Send ${s.staff}'s open ${year} renewal bonus (${money(s.open_total)}) to bonus as ONE separate payout?`)}>Send to bonus</button>}
              </div>
            ))}
          </div>
          <div style={box(c)} data-testid="rb-payouts">
            <H c={c}>Renewal bonus payouts</H>
            {!data.payouts.length && <div style={{ fontSize: fs(13), color: c.sub }}>None yet.</div>}
            {data.payouts.map((p) => (
              <div key={p.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: fs(13), padding: '3px 0', flexWrap: 'wrap' }} data-testid={`rb-payout-${p.id}`}>
                <strong>#{p.id}</strong><span>{p.staff}</span><span>{money(p.total)}</span><span>{p.lines} client(s)</span>
                <span style={{ color: c.sub }}>{p.status === 'paid' ? `paid ${p.paid_label}${p.delayed ? ' (a part is still to come)' : ''}` : p.finalized ? `finalized ${p.finalized_label} — mark it paid on Bonuses → Payout batches` : 'sent — not finalized'}</span>
                {p.status === 'open' && !p.finalized && <button style={btn(c, true, busy)} disabled={busy} data-testid={`rb-finalize-${p.id}`}
                  onClick={() => act(() => rb().finalize(p.id), `Finalize renewal payout #${p.id}?`)}>Finalize</button>}
                {(p.status === 'open' || p.delayed) && <button style={btn(c, false, busy)} disabled={busy} data-testid={`rb-revert-${p.id}`}
                  onClick={() => act(() => rb().revert(p.id), `Revert payout #${p.id}? Its lines go back to open (refused if a part is in Books).`)}>Revert</button>}
              </div>
            ))}
          </div>
          <div style={box(c)} data-testid="rb-flags">
            <H c={c}>Duplicate flags</H>
            {!data.flags.length && <div style={{ fontSize: fs(13), color: c.sub }}>None.</div>}
            {data.flags.map((fl, i) => (
              <div key={i} style={{ fontSize: fs(13), padding: '3px 0' }} data-testid={`rb-flag-${fl.kind}`}>
                <strong style={{ color: '#b45309' }}>{fl.label}</strong>: {fl.clients.map((x) => `${x.client} (${x.staff.join(' + ') || '—'})`).join(' · ')}</div>
            ))}
          </div>
          <div style={box(c)} data-testid="rb-cancellations">
            <H c={c}>Policies terminated / cancelled (from the daily refresh)</H>
            {!data.cancellations.length && <div style={{ fontSize: fs(13), color: c.sub }}>None.</div>}
            {data.cancellations.map((x) => (
              <div key={x.bonus_id} style={{ fontSize: fs(13), padding: '3px 0', display: 'flex', gap: 10, alignItems: 'center' }}>
                <span>{x.client}</span><span>{x.status}</span><span style={{ color: c.sub }}>flagged {x.flagged_label}</span>
                {x.reviewed ? <span style={{ color: c.sub }}>reviewed by {x.reviewed_by} {x.reviewed_label}</span>
                  : <button style={btn(c, false, busy)} disabled={busy} onClick={() => act(() => rb().reviewCancel(x.bonus_id))} data-testid={`rb-review-${x.bonus_id}`}>Mark reviewed</button>}
              </div>
            ))}
            <div style={{ fontSize: fs(12), color: c.sub, marginTop: 6 }}>Amounts never change by themselves — add an adjustment on the client's bonus (e.g. −$5 per member per cancelled month).</div>
          </div>
          <div style={box(c)} data-testid="rb-amounts">
            <H c={c}>Amount list (per member, max $60)</H>
            <input value={amountsText} onChange={(e) => setAmountsText(e.target.value)} style={input(c, 220)} aria-label="Amounts" data-testid="rb-amounts-input" />
            <button style={{ ...btn(c, true, busy), marginLeft: 8 }} disabled={busy} data-testid="rb-amounts-save"
              onClick={() => act(() => rb().setAmounts(amountsText.split(',').map((x) => x.trim()).filter(Boolean)))}>Save</button>
          </div>
        </>
      )}
    </div>
  );
}
